import React from 'react';
import { triggerUiHaptic, UiHapticType } from '../utils/webHaptics';

export type WebCtaVariant = 'primary' | 'neutral' | 'contrast' | 'success' | 'danger' | 'muted' | 'info';

export interface WebCtaProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    loading?: boolean;
    disabled?: boolean;
    variant?: WebCtaVariant;
    fullWidth?: boolean;
    className?: string;
    haptic?: UiHapticType;
}

export const WebCta: React.FC<WebCtaProps> = ({
    title,
    subtitle,
    icon,
    iconPosition = 'left',
    loading = false,
    disabled = false,
    variant = 'primary',
    fullWidth = true,
    className = '',
    haptic = 'impactLight',
    onClick,
    type = 'button',
    ...props
}) => {
    const isPressDisabled = disabled || loading;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isPressDisabled) return;
        if (haptic && haptic !== 'none') {
            triggerUiHaptic(haptic as UiHapticType);
        }
        if (onClick) {
            onClick(e);
        }
    };

    const variantStyles: Record<WebCtaVariant, string> = {
        primary: 'bg-[#0D9488] hover:bg-[#0F766E] text-white border-transparent',
        contrast: 'bg-[#0F172A] hover:bg-[#1E293B] text-white border-transparent',
        neutral: 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-transparent',
        success: 'bg-emerald-600 hover:bg-emerald-700 text-white border-transparent',
        danger: 'bg-red-50 hover:bg-red-100 text-red-600 border-red-200',
        muted: 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed',
        info: 'bg-[#008CFF] hover:bg-[#0A7FE8] text-white border-transparent',
    };

    const currentVariantStyle = isPressDisabled && variant !== 'muted'
        ? 'bg-slate-200/80 text-slate-400 border-slate-200 cursor-not-allowed'
        : variantStyles[variant];

    return (
        <button
            type={type}
            disabled={isPressDisabled}
            onClick={handleClick}
            className={`h-12 min-h-[48px] px-6 rounded-full font-extrabold text-[15px] tracking-[-0.01em] transition-all flex items-center justify-center gap-2 select-none border active:scale-[0.98] ${
                fullWidth ? 'w-full' : ''
            } ${currentVariantStyle} ${className}`}
            {...props}
        >
            {loading ? (
                <div className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0" />
                    <span>Loading...</span>
                </div>
            ) : (
                <>
                    {icon && iconPosition === 'left' && (
                        <span className="shrink-0 flex items-center">{icon}</span>
                    )}
                    <div className="flex flex-col items-center justify-center leading-tight">
                        <span className="truncate">{title}</span>
                        {subtitle && (
                            <span className="text-[11px] font-semibold opacity-80 mt-0.5">{subtitle}</span>
                        )}
                    </div>
                    {icon && iconPosition === 'right' && (
                        <span className="shrink-0 flex items-center">{icon}</span>
                    )}
                </>
            )}
        </button>
    );
};

export default WebCta;
